import { CSSProperties } from "react";

import { SourceBox, SourceConfidence, SourceLocation } from "models";

export type SourcePageTab = {
  key: string;
  page: number;
  section: SourceLocation["section"];
  confidence: SourceConfidence;
  /** One location per row to draw; a page-only location has no row. */
  locations: SourceLocation[];
};

const CONFIDENCE_ORDER: SourceConfidence[] = ["high", "multi", "low", "page_only"];

const rowKey = (row: SourceBox | null | undefined) => (row ? `${row.x0},${row.y0},${row.x1},${row.y1}` : "page");

const strongestConfidence = (locations: SourceLocation[]): SourceConfidence =>
  CONFIDENCE_ORDER.find((confidence) => locations.some((location) => location.confidence === confidence)) ?? "page_only";

/**
 * Groups source locations into one tab per page and PL section (Section XI parcel rows, Section XII crop rows).
 * A field and its crops often point at the same row, so locations on one row are merged into one with all cells.
 */
export const buildSourcePageTabs = (locations: SourceLocation[]): SourcePageTab[] => {
  const tabs = new Map<string, SourceLocation[]>();

  locations.forEach((location) => {
    const tabKey = `${location.page}-${location.section ?? ""}`;
    const tabLocations = tabs.get(tabKey) ?? [];
    const sameRowIndex = tabLocations.findIndex((existing) => rowKey(existing.row) === rowKey(location.row));

    if (sameRowIndex === -1) tabLocations.push(location);
    else tabLocations[sameRowIndex] = { ...tabLocations[sameRowIndex], cells: { ...tabLocations[sameRowIndex].cells, ...location.cells } };

    tabs.set(tabKey, tabLocations);
  });

  return [...tabs.entries()]
    .map(([key, tabLocations]) => {
      const rowLocations = tabLocations.filter((location) => location.row);
      const drawnLocations = rowLocations.length ? rowLocations : tabLocations;
      return {
        key,
        page: tabLocations[0].page,
        section: tabLocations[0].section,
        confidence: strongestConfidence(drawnLocations),
        locations: drawnLocations,
      };
    })
    .sort((first, second) => first.page - second.page);
};

/** Boxes are 0-1 of the page, so percentages place them on the page at any render size. */
export const toSourceBoxStyle = ({ x0, y0, x1, y1 }: SourceBox): CSSProperties => ({
  left: `${x0 * 100}%`,
  top: `${y0 * 100}%`,
  width: `${(x1 - x0) * 100}%`,
  height: `${(y1 - y0) * 100}%`,
});
